import { useMemo } from "react";
import { Calendar, History, User } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import DOMPurify from "dompurify";
import { type Section, type SectionVersion } from "~/api/types";
import { DocumentVersionStatus } from "~/config/types";
import { Badge } from "~/atoms/badge";
import { formatDate } from "~/helpers/date";
import { FieldStatus } from "~/molecules/FieldStatus";
import { OutdatedVersionsBadge } from "~/organisms/SectionsPanel";

interface VersionPanelProps {
  section: Section;
  selectedVersion: SectionVersion | null;
  onVersionClick: (version: SectionVersion) => void;
}

export const VersionsPanel = ({
  section,
  selectedVersion,
  onVersionClick,
}: VersionPanelProps) => {
  const { t } = useTranslation();

  // Sort versions by createdAt descending (newest first)
  const sectionVersions = useMemo(() => {
    return section.sectionVersions.toSorted(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [section.sectionVersions]);

  const numberOfVersions = sectionVersions.length;

  return (
    <div className="flex flex-1 flex-col gap-2">
      <p className="text-2xl font-semibold">{section.name}</p>
      <div className="text-muted-foreground flex flex-col gap-1">
        <div className="flex gap-2">
          <History className="size-4" />
          <p className="text-sm">
            {t(($) => $.commons.sections.versionHistoryWithCount, {
              count: numberOfVersions,
            })}
          </p>
        </div>
        <p className="text-xs">
          {t(($) => $.commons.sections.selectVersionToView)}
        </p>
      </div>

      <div className="bg-background border-item-border overflow-auto rounded-2xl border">
        {sectionVersions.map((version, index) => (
          <div
            key={version.id}
            className={`border-item-border hover:bg-muted/50 border-b hover:cursor-pointer ${version.id === selectedVersion?.id && "bg-muted"}`}
            onClick={() => onVersionClick(version)}
          >
            <div className="flex flex-col p-4">
              <div className="flex items-center justify-between pb-2">
                <p className="text-base font-semibold">
                  {t(($) => $.commons.sections.version)}{" "}
                  {numberOfVersions - index}
                </p>
                <div className="flex gap-2">
                  {index === 0 && (
                    <Badge variant="info">
                      {t(($) => $.commons.sections.latest)}
                    </Badge>
                  )}
                </div>
              </div>
              <div className="flex gap-4 pb-4">
                <div className="text-muted-foreground flex gap-1">
                  <Calendar className="size-3" />
                  <p className="text-xs font-normal">
                    {t(($) => $.commons.sections.createdAt)}
                  </p>
                  <p className="text-xs font-semibold">
                    {formatDate(version.createdAt)}
                  </p>
                </div>
                <div className="text-muted-foreground flex gap-1">
                  <User className="size-3" />
                  <p className="text-xs font-normal">
                    {t(($) => $.commons.sections.createdBy)}
                  </p>
                  <p className="text-xs font-semibold">
                    {version.createdBy.firstName}
                  </p>
                </div>
              </div>
              {version.usedInDocumentVersions.length > 0 ? (
                <div className="flex flex-col gap-2">
                  <p className="text-muted-foreground text-xs font-semibold">
                    {t(($) => $.commons.sections.usedInDocuments)}
                  </p>
                  {version.usedInDocumentVersions.map((dv) => {
                    const isOutdated =
                      index !== 0 && dv.status === DocumentVersionStatus.draft;
                    return (
                      <div key={dv.id} className="flex items-center gap-4">
                        <FieldStatus status={dv.status} />
                        <Link
                          to={`/documents/${dv.documentId}?versionId=${dv.id}`}
                          className="text-foreground text-sm hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {dv.title}
                        </Link>
                        {isOutdated && <OutdatedVersionsBadge />}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-muted-foreground text-xs italic">
                  {t(($) => $.commons.sections.noDocuments)}
                </p>
              )}
            </div>
            {selectedVersion?.id === version.id && (
              <div className="px-4 pb-4" onClick={(e) => e.stopPropagation()}>
                <p className="text-muted-foreground text-xs">
                  {t(($) => $.commons.sections.preview)}
                </p>
                <div className="bg-background border-item-border cursor-text rounded-2xl border p-6">
                  <div
                    style={{ zoom: 0.8 }}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(selectedVersion.htmlContent),
                    }}
                  ></div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
